import type {
  CommandState,
  FleetCommand,
  FleetDevice,
  NetworkInterfaceStatus,
} from '@manikin-overmind/protocol';
import { Fragment } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useAuth } from './auth';
import {
  commands,
  connectFleetSocket,
  connectionState,
  createRebootCommand,
  deviceLabel,
  devices,
  distinctManikinSerial,
  formatManikinId,
  formatProductId,
  lastLiveUpdate,
  loadError,
  loadFleet,
  loading,
  manikinChipTone,
  manikinName,
  onlineCount,
  primaryNetworkAddress,
  primaryNetworkInterface,
  sendUiMessage,
  sortedDevices,
  subscribeToUiMessages,
  updateDeviceDisplayName,
  usefulAddresses,
  usefulNetworkInterfaces,
} from './fleet';
import {
  appendCanFrames,
  appendServiceLogLines,
  type CanFrame,
  canFramesToCsv,
  controlService,
  type ExtendedDeviceStatus,
  filterCanFrames,
  installDeveloperSshKey,
  normalizeCanFrame,
  parseOpenSshPublicKey,
  readOperationalStatus,
  refreshOperationalStatus,
  type ServiceAction,
  type ServiceStatus,
  sshFingerprint,
} from './operations';

type ViewMode = 'cards' | 'table';
type Toast = { kind: 'success' | 'error'; message: string };

const terminalCommandStates = new Set<CommandState>(['completed', 'rejected', 'failed', 'expired']);
const TERMINAL_FEEDBACK_MS = 8_000;

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  const paths: Record<string, preact.JSX.Element> = {
    search: <path d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />,
    grid: <path d="M4 4h6v6H4V4Zm10 0h6v6h-6V4ZM4 14h6v6H4v-6Zm10 0h6v6h-6v-6Z" />,
    list: <path d="M9 5h11M9 12h11M9 19h11M4 5h.01M4 12h.01M4 19h.01" />,
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    power: <path d="M12 2v10m5.66-6.66a8 8 0 1 1-11.32 0" />,
    chevron: <path d="m9 18 6-6-6-6" />,
    wifi: <path d="M5 12.55a11 11 0 0 1 14 0M8.5 16a6 6 0 0 1 7 0M12 20h.01M2 9a16 16 0 0 1 20 0" />,
    ethernet: <path d="M5 7h14v9H5V7Zm3 9v3m8-3v3M8 11h.01M12 11h.01M16 11h.01" />,
    can: <path d="M5 9h14l2 4v5H3v-5l2-4Zm2 9v2m10-2v2M7 14h.01M17 14h.01" />,
    loopback: <path d="M8 7h8a4 4 0 0 1 0 8H8m0-8 3-3M8 7l3 3m5 5-3-3m3 3-3 3" />,
    other: (
      <path d="M12 3v4m0 10v4M3 12h4m10 0h4m-6.17-7.83-1.42 1.42m-2.82 12.82-1.42 1.42m0-15.66 1.42 1.42m2.82 12.82 1.42 1.42" />
    ),
    clock: <path d="M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    edit: <path d="m4 20 4.2-1 10.6-10.6a2.1 2.1 0 0 0-3-3L5.2 16 4 20Zm10.4-13 3 3" />,
    bluetooth: <path d="m7 7 10 10-5 4V3l5 4L7 17" />,
    key: <path d="M15 7a5 5 0 1 1-3.54 8.54L3 24v-4l2-2h3v-3l1.46-1.46A5 5 0 0 1 15 7Z" />,
    play: <path d="m8 5 11 7-11 7V5Z" />,
    stop: <path d="M7 7h10v10H7V7Z" />,
    pause: <path d="M8 5v14m8-14v14" />,
    upload: <path d="M12 16V3m-5 5 5-5 5 5M5 21h14" />,
    download: <path d="M12 3v13m-5-5 5 5 5-5M5 21h14" />,
    terminal: <path d="m4 6 5 5-5 5m7 0h9" />,
  };
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      {paths[name] ?? paths.other}
    </svg>
  );
}

export function formatRelative(value: string | null, now: number): string {
  if (!value) return 'Never';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'Unknown';
  const seconds = Math.round((timestamp - now) / 1_000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}

export function formatUptime(seconds?: number): string {
  if (seconds === undefined) return 'Unknown';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function latestCommandFor(deviceId: string, now: number): FleetCommand | undefined {
  const latest = commands.value
    .filter((command) => command.deviceId === deviceId)
    .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))[0];
  if (!latest || !terminalCommandStates.has(latest.state)) return latest;
  const terminalAt = latest.completedAt ?? latest.acceptedAt ?? latest.sentAt ?? latest.requestedAt;
  return now - Date.parse(terminalAt) <= TERMINAL_FEEDBACK_MS ? latest : undefined;
}

function commandLabel(command: FleetCommand): string {
  const action = command.action.replaceAll('_', ' ');
  return command.state === 'completed' ? `${action} complete` : `${action} ${command.state}`;
}

function CommandBadge({ command }: { command: FleetCommand }) {
  const active = !terminalCommandStates.has(command.state);
  return (
    <span class={`command-badge command-${command.state}`} title={command.detail ?? undefined}>
      {active && <span class="mini-spinner" />}
      {commandLabel(command)}
    </span>
  );
}

function InterfaceIcon({ kind }: { kind: NetworkInterfaceStatus['kind'] }) {
  return <Icon name={kind} size={17} />;
}

function InterfaceRow({ network }: { network: NetworkInterfaceStatus }) {
  const addresses = usefulAddresses(network);
  return (
    <div class="interface-row">
      <div class={`interface-icon interface-${network.kind}`}>
        <InterfaceIcon kind={network.kind} />
      </div>
      <div class="interface-body">
        <div class="interface-heading">
          <strong>{network.name}</strong>
          <span class={`link-state ${network.up ? 'is-up' : ''}`}>
            {network.up ? (network.carrier === false ? 'No carrier' : 'Up') : 'Down'}
          </span>
          {network.defaultRoute && <span class="route-badge">Default</span>}
        </div>
        {network.ssid && (
          <div class="wifi-line">
            <Icon name="wifi" size={14} />
            <strong>{network.ssid}</strong>
            {network.signalDbm !== undefined && <span>{network.signalDbm} dBm</span>}
            {network.frequencyMhz !== undefined && <span>{network.frequencyMhz} MHz</span>}
          </div>
        )}
        <div class="interface-addresses">
          {addresses.length > 0 ? (
            addresses.map((address) => (
              <code key={`${address.family}-${address.address}`}>
                {address.address}/{address.prefixLength}
              </code>
            ))
          ) : (
            <span class="muted">No IP address</span>
          )}
        </div>
        <div class="interface-meta">
          {network.macAddress && <span>MAC {network.macAddress}</span>}
          {network.driver && <span>Driver {network.driver}</span>}
          {network.rxBitrateMbps !== undefined && <span>↓ {network.rxBitrateMbps} Mbps</span>}
          {network.txBitrateMbps !== undefined && <span>↑ {network.txBitrateMbps} Mbps</span>}
        </div>
      </div>
    </div>
  );
}

function StateDot({ active }: { active: boolean }) {
  return <span class={`state-dot ${active ? 'active' : ''}`} aria-hidden="true" />;
}

function BluetoothPanel({ status }: { status: ExtendedDeviceStatus }) {
  const operational = readOperationalStatus(status);
  const bluetooth = operational?.bluetooth;
  if (!operational || !bluetooth) {
    return <div class="operation-empty">Operational telemetry has not been collected yet.</div>;
  }
  if (!bluetooth.available) {
    return (
      <div class="operation-empty">
        Bluetooth unavailable{bluetooth.error ? `: ${bluetooth.error}` : ' on this device.'}
      </div>
    );
  }
  return (
    <div class="bluetooth-panel">
      <div class="status-strip">
        <span>
          <StateDot active={bluetooth.powered} />
          {bluetooth.powered ? 'Powered' : 'Off'}
        </span>
        <span>
          <StateDot active={bluetooth.advertising} />
          {bluetooth.advertising ? 'Advertising' : 'Not advertising'}
        </span>
        <span>{bluetooth.connectedPeerCount} connected</span>
      </div>
      {(bluetooth.name || bluetooth.address) && (
        <div class="adapter-identity">
          <strong>{bluetooth.name ?? 'Bluetooth adapter'}</strong>
          {bluetooth.address && <code>{bluetooth.address}</code>}
        </div>
      )}
      {bluetooth.managerProcess && (
        <div class="bluetooth-manager">
          <span>bluetoothd</span>
          <strong>{bluetooth.managerProcess.name || 'bluetoothd'}</strong>
          <code>PID {bluetooth.managerProcess.pid}</code>
          {bluetooth.managerProcess.args && bluetooth.managerProcess.args.length > 0 && (
            <code title={bluetooth.managerProcess.args.join(' ')}>
              {bluetooth.managerProcess.args.join(' ')}
            </code>
          )}
        </div>
      )}
      {bluetooth.applicationProcess && (
        <div class="bluetooth-manager">
          <span>Advertising app</span>
          <strong>{bluetooth.applicationProcess.name || 'ble-server-levo'}</strong>
          <code>PID {bluetooth.applicationProcess.pid}</code>
          {(bluetooth.applicationProcess.command || bluetooth.applicationProcess.cwd) && (
            <code title={bluetooth.applicationProcess.command || bluetooth.applicationProcess.cwd}>
              {bluetooth.applicationProcess.command || bluetooth.applicationProcess.cwd}
            </code>
          )}
        </div>
      )}
      {bluetooth.advertising && !bluetooth.applicationProcess && (
        <div class="bluetooth-manager">
          <span>Advertising app</span>
          <strong>Unknown BlueZ client</strong>
        </div>
      )}
      <div class="peer-list">
        {bluetooth.connectedPeers.length === 0 ? (
          <span class="muted">No connected BLE peers.</span>
        ) : (
          bluetooth.connectedPeers.map((peer) => (
            <div key={peer.address} class="peer-row">
              <span>
                <StateDot active />
                {peer.name ?? 'Unnamed device'}
              </span>
              <code>{peer.address}</code>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ConfirmationDialog({
  title,
  description,
  detailLabel,
  detail,
  confirmLabel,
  icon,
  danger = false,
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  detailLabel: string;
  detail: string;
  confirmLabel: string;
  icon: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && !submitting && onClose();
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, submitting]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The operation failed.');
      setSubmitting(false);
    }
  };

  return (
    <div class="dialog-backdrop">
      <div class="dialog" role="alertdialog" aria-modal="true" aria-labelledby="operation-dialog-title">
        <button class="dialog-close" type="button" aria-label="Close" onClick={onClose} disabled={submitting}>
          <Icon name="close" />
        </button>
        <div class={`dialog-icon operational ${danger ? 'danger' : ''}`}>
          <Icon name={icon} size={24} />
        </div>
        <h2 id="operation-dialog-title">{title}</h2>
        <p>{description}</p>
        <div class="dialog-identity">
          <span>{detailLabel}</span>
          <code>{detail}</code>
        </div>
        {error && (
          <div class="dialog-error" role="alert">
            {error}
          </div>
        )}
        <div class="dialog-actions">
          <button
            ref={cancelRef}
            class="secondary-button"
            type="button"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            class={`operation-confirm-button ${danger ? 'danger' : ''}`}
            type="button"
            disabled={submitting}
            onClick={() => void submit()}
          >
            {submitting ? <span class="spinner" /> : <Icon name={icon} size={16} />}
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceRow({
  device,
  service,
  enabled,
  logsEnabled,
  onLogs,
}: {
  device: FleetDevice;
  service: ServiceStatus;
  enabled: boolean;
  logsEnabled: boolean;
  onLogs: () => void;
}) {
  const [working, setWorking] = useState<ServiceAction | null>(null);
  const [pendingAction, setPendingAction] = useState<ServiceAction | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const active = service.activeState === 'active';
  useEffect(() => {
    if (!result) return;
    const timer = window.setTimeout(() => setResult(null), TERMINAL_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [result]);
  const runAction = async (action: ServiceAction) => {
    setWorking(action);
    setResult(null);
    try {
      await controlService(device.id, service.name, action);
      setResult(`${action} requested`);
    } catch (cause) {
      throw cause instanceof Error ? cause : new Error(`${action} failed`);
    } finally {
      setWorking(null);
    }
  };
  return (
    <div class="service-row">
      <div class="service-state">
        <StateDot active={active} />
        <span>
          <strong>{service.name.replace(/\.service$/, '')}</strong>
          <small>
            {service.description ?? service.name}
            {!service.controllable && <span class="service-read-only"> · read only</span>}
          </small>
        </span>
      </div>
      <div class="service-state-copy">
        <strong>{service.activeState}</strong>
        <span>{service.subState}</span>
      </div>
      <div class="service-actions">
        {service.controllable &&
          (['start', 'stop', 'restart'] as const).map((action) => (
            <button
              key={action}
              type="button"
              disabled={
                !enabled ||
                working !== null ||
                (action === 'start' && (active || service.canStart === false)) ||
                (action === 'stop' && (!active || service.canStop === false))
              }
              onClick={() => setPendingAction(action)}
            >
              {working === action && <span class="mini-spinner" />}
              {action}
            </button>
          ))}
        <button type="button" disabled={!logsEnabled} onClick={onLogs}>
          <Icon name="terminal" size={12} /> Logs
        </button>
      </div>
      {result && (
        <span class="action-result" role="status">
          {result}
        </span>
      )}
      {pendingAction && (
        <ConfirmationDialog
          title={`${pendingAction[0]?.toUpperCase()}${pendingAction.slice(1)} ${service.name.replace(/\.service$/, '')}?`}
          description={`${deviceLabel(device)} will be asked to ${pendingAction} this service immediately. The status panel will refresh after the command finishes.`}
          detailLabel="Target service"
          detail={service.name}
          confirmLabel={`${pendingAction[0]?.toUpperCase()}${pendingAction.slice(1)} service`}
          icon={pendingAction === 'stop' ? 'stop' : pendingAction === 'restart' ? 'refresh' : 'play'}
          danger={pendingAction === 'stop'}
          onClose={() => setPendingAction(null)}
          onConfirm={() => runAction(pendingAction)}
        />
      )}
    </div>
  );
}

function ServiceLogsDialog({
  device,
  service,
  onClose,
}: {
  device: FleetDevice;
  service: ServiceStatus;
  onClose: () => void;
}) {
  const [streamState, setStreamState] = useState<'starting' | 'started' | 'stopped' | 'error'>('starting');
  const [detail, setDetail] = useState<string | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [paused, setPaused] = useState(false);
  const [dropped, setDropped] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadBytes, setDownloadBytes] = useState(0);
  const pausedRef = useRef(false);
  const subscribedRef = useRef(false);
  const downloadRequestRef = useRef<string | null>(null);
  const downloadChunksRef = useRef<string[]>([]);
  const feedRef = useRef<HTMLDivElement>(null);
  pausedRef.current = paused;

  useEffect(() => {
    const dispose = subscribeToUiMessages((message) => {
      if (!message || typeof message !== 'object') return;
      const event = message as Record<string, unknown>;
      if (event.type === 'fleet_snapshot') {
        if (downloadRequestRef.current) {
          downloadRequestRef.current = null;
          downloadChunksRef.current = [];
          setDownloading(false);
          setDownloadBytes(0);
          setDetail('The log download was interrupted by a dashboard reconnect.');
        }
        if (subscribedRef.current) {
          setStreamState('starting');
          if (
            !sendUiMessage({
              type: 'service_log_subscribe',
              deviceId: device.id,
              service: service.name,
            })
          ) {
            subscribedRef.current = false;
            setStreamState('error');
            setDetail('The dashboard WebSocket is not connected.');
          }
        }
        return;
      }
      if (event.deviceId !== device.id || event.service !== service.name) return;
      if (event.type === 'service_log_state') {
        const state = event.state;
        if (state === 'started' || state === 'stopped' || state === 'error') {
          setStreamState(state);
          setDetail(typeof event.detail === 'string' ? event.detail : null);
        }
      } else if (event.type === 'service_log_lines' && Array.isArray(event.lines)) {
        const incoming = event.lines.filter((line): line is string => typeof line === 'string');
        if (!pausedRef.current) setLines((current) => appendServiceLogLines(current, incoming));
        const droppedCount = event.dropped;
        if (typeof droppedCount === 'number') setDropped((current) => current + droppedCount);
      } else if (
        event.type === 'service_log_download_chunk' &&
        event.requestId === downloadRequestRef.current
      ) {
        const chunk = event.chunk;
        if (typeof chunk === 'string') {
          downloadChunksRef.current.push(chunk);
          setDownloadBytes((current) => current + new Blob([chunk]).size);
        }
        if (event.done === true) {
          const error = typeof event.error === 'string' && event.error ? event.error : null;
          if (error) {
            setDetail(error);
          } else {
            const blob = new Blob(downloadChunksRef.current, { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const timestamp = new Date().toISOString().replaceAll(':', '-').replace('.', '-');
            link.href = url;
            link.download = `${deviceLabel(device).replace(/[^a-z0-9_-]+/gi, '-')}-${service.name}-${timestamp}.log`;
            document.body.append(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 0);
          }
          downloadRequestRef.current = null;
          downloadChunksRef.current = [];
          setDownloading(false);
        }
      }
    });
    subscribedRef.current = sendUiMessage({
      type: 'service_log_subscribe',
      deviceId: device.id,
      service: service.name,
    });
    if (!subscribedRef.current) {
      setStreamState('error');
      setDetail('The dashboard WebSocket is not connected.');
    }
    return () => {
      if (subscribedRef.current) {
        sendUiMessage({ type: 'service_log_unsubscribe', deviceId: device.id, service: service.name });
      }
      dispose();
    };
  }, [device.id, service.name]);

  useEffect(() => {
    if (!paused && feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [lines, paused]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && !downloading && onClose();
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [downloading, onClose]);

  const toggleStream = () => {
    if (streamState === 'started') {
      sendUiMessage({ type: 'service_log_unsubscribe', deviceId: device.id, service: service.name });
      subscribedRef.current = false;
      setStreamState('stopped');
    } else {
      setDetail(null);
      setStreamState('starting');
      subscribedRef.current = sendUiMessage({
        type: 'service_log_subscribe',
        deviceId: device.id,
        service: service.name,
      });
      if (!subscribedRef.current) {
        setStreamState('error');
        setDetail('The dashboard WebSocket is not connected.');
      }
    }
  };

  const downloadAll = () => {
    const requestId = crypto.randomUUID();
    downloadRequestRef.current = requestId;
    downloadChunksRef.current = [];
    setDownloadBytes(0);
    setDownloading(true);
    setDetail(null);
    if (
      !sendUiMessage({
        type: 'service_log_download',
        deviceId: device.id,
        service: service.name,
        requestId,
      })
    ) {
      downloadRequestRef.current = null;
      setDownloading(false);
      setDetail('The dashboard WebSocket is not connected.');
    }
  };

  return (
    <div class="dialog-backdrop">
      <div class="dialog log-dialog" role="dialog" aria-modal="true" aria-labelledby="service-logs-title">
        <button
          class="dialog-close"
          type="button"
          aria-label="Close"
          onClick={onClose}
          disabled={downloading}
        >
          <Icon name="close" />
        </button>
        <div class="log-dialog-heading">
          <div class="dialog-icon operational">
            <Icon name="terminal" size={24} />
          </div>
          <div>
            <span>Live journal</span>
            <h2 id="service-logs-title">{service.name}</h2>
            <code>{deviceLabel(device)}</code>
          </div>
        </div>
        <div class="log-toolbar">
          <button type="button" onClick={toggleStream} disabled={streamState === 'starting'}>
            <Icon name={streamState === 'started' ? 'stop' : 'play'} size={13} />
            {streamState === 'started' ? 'Stop tail' : 'Tail logs'}
          </button>
          <button type="button" onClick={() => setPaused(!paused)} disabled={streamState !== 'started'}>
            <Icon name={paused ? 'play' : 'pause'} size={13} /> {paused ? 'Resume' : 'Pause'}
          </button>
          <button type="button" onClick={() => setLines([])} disabled={lines.length === 0}>
            Clear
          </button>
          <button class="download-logs-button" type="button" onClick={downloadAll} disabled={downloading}>
            {downloading ? <span class="mini-spinner" /> : <Icon name="download" size={13} />}
            {downloading
              ? `Downloading ${Math.max(1, Math.round(downloadBytes / 1024))} KB…`
              : 'Download all'}
          </button>
          <span class={`stream-state stream-${streamState}`}>
            <StateDot active={streamState === 'started'} /> {streamState}
          </span>
          <span>
            {lines.length}/2000 lines · {dropped} dropped
          </span>
        </div>
        {detail && (
          <div class="dialog-error" role="alert">
            {detail}
          </div>
        )}
        <div ref={feedRef} class="service-log-feed" aria-live="off">
          {lines.length === 0 ? (
            <span>Waiting for journal output…</span>
          ) : (
            lines.map((line, index) => <div key={`${index}-${line.slice(0, 32)}`}>{line || ' '}</div>)
          )}
        </div>
        <p class="log-retention-note">
          Tail starts with the latest 500 lines. Download all exports the complete journal retained for this
          unit on the device.
        </p>
      </div>
    </div>
  );
}

function ServicesPanel({ device, status }: { device: FleetDevice; status: ExtendedDeviceStatus }) {
  const [logService, setLogService] = useState<ServiceStatus | null>(null);
  const services = readOperationalStatus(status)?.services ?? [];
  const canControl = device.presence === 'online' && status.capabilities?.serviceControl === true;
  const canReadLogs = device.presence === 'online' && status.capabilities?.serviceLogs === true;
  if (services.length === 0) return <div class="operation-empty">No service status reported yet.</div>;
  return (
    <div class="service-list">
      {services.map((service) => (
        <ServiceRow
          key={service.name}
          device={device}
          service={service}
          enabled={canControl}
          logsEnabled={canReadLogs}
          onLogs={() => setLogService(service)}
        />
      ))}
      {!canControl && (
        <p class="capability-note">
          Service controls require an online agent started with service control enabled.
        </p>
      )}
      {logService && (
        <ServiceLogsDialog device={device} service={logService} onClose={() => setLogService(null)} />
      )}
    </div>
  );
}

function CanMonitor({ device, status }: { device: FleetDevice; status: ExtendedDeviceStatus }) {
  const [streamState, setStreamState] = useState<'stopped' | 'starting' | 'started' | 'error'>('stopped');
  const [detail, setDetail] = useState<string | null>(null);
  const [frames, setFrames] = useState<CanFrame[]>([]);
  const [filters, setFilters] = useState({ id: '', payload: '' });
  const [paused, setPaused] = useState(false);
  const [dropped, setDropped] = useState(0);
  const [rate, setRate] = useState(0);
  const activeRef = useRef(false);
  const pausedRef = useRef(false);
  const receivedThisSecond = useRef(0);
  pausedRef.current = paused;

  useEffect(() => {
    const dispose = subscribeToUiMessages((message) => {
      if (!message || typeof message !== 'object') return;
      const event = message as Record<string, unknown>;
      if (event.type === 'fleet_snapshot') {
        if (activeRef.current) {
          setStreamState('starting');
          if (!sendUiMessage({ type: 'can_subscribe', deviceId: device.id })) {
            activeRef.current = false;
            setStreamState('error');
            setDetail('The dashboard WebSocket is not connected.');
          }
        }
        return;
      }
      if (event.deviceId !== device.id) return;
      if (event.type === 'can_stream_state') {
        const next = event.state;
        if (next === 'started' || next === 'stopped' || next === 'error') {
          activeRef.current = next === 'started';
          setStreamState(next);
          setDetail(typeof event.detail === 'string' ? event.detail : null);
        }
      } else if (event.type === 'can_frames' && Array.isArray(event.frames)) {
        const incoming = event.frames
          .map(normalizeCanFrame)
          .filter((frame): frame is CanFrame => frame !== null);
        receivedThisSecond.current += incoming.length;
        if (!pausedRef.current) setFrames((current) => appendCanFrames(current, incoming));
        const droppedCount = event.dropped;
        if (typeof droppedCount === 'number') setDropped((current) => current + droppedCount);
      }
    });
    const meter = window.setInterval(() => {
      setRate(receivedThisSecond.current);
      receivedThisSecond.current = 0;
    }, 1_000);
    return () => {
      if (activeRef.current) sendUiMessage({ type: 'can_unsubscribe', deviceId: device.id });
      window.clearInterval(meter);
      dispose();
    };
  }, [device.id]);

  const start = () => {
    setDetail(null);
    setStreamState('starting');
    if (!sendUiMessage({ type: 'can_subscribe', deviceId: device.id })) {
      setStreamState('error');
      setDetail('The dashboard WebSocket is not connected.');
    }
  };
  const stop = () => {
    sendUiMessage({ type: 'can_unsubscribe', deviceId: device.id });
    activeRef.current = false;
    setStreamState('stopped');
  };
  const exportCsv = () => {
    const blob = new Blob([canFramesToCsv(frames)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replaceAll(':', '-').replace('.', '-');
    link.href = url;
    link.download = `${deviceLabel(device).replace(/[^a-z0-9_-]+/gi, '-')}-can0-${timestamp}.csv`;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const visibleFrames = useMemo(() => filterCanFrames(frames, filters), [frames, filters]);
  const supported = status.capabilities?.canStreaming ?? status.capabilities?.canMonitor ?? false;

  return (
    <div class="can-monitor">
      <div class="can-toolbar">
        <button
          class="operation-primary"
          type="button"
          disabled={!supported || device.presence !== 'online' || streamState === 'starting'}
          onClick={streamState === 'started' ? stop : start}
        >
          <Icon name={streamState === 'started' ? 'stop' : 'play'} size={14} />
          {streamState === 'started'
            ? 'Stop stream'
            : streamState === 'starting'
              ? 'Starting…'
              : 'Stream can0'}
        </button>
        <button type="button" disabled={streamState !== 'started'} onClick={() => setPaused(!paused)}>
          <Icon name={paused ? 'play' : 'pause'} size={13} /> {paused ? 'Resume' : 'Pause'}
        </button>
        <button type="button" disabled={frames.length === 0} onClick={() => setFrames([])}>
          Clear
        </button>
        <button type="button" disabled={frames.length === 0} onClick={exportCsv}>
          <Icon name="download" size={13} /> Export CSV
        </button>
        <span class={`stream-state stream-${streamState}`}>
          <StateDot active={streamState === 'started'} />
          {streamState}
        </span>
        <span>
          {rate} frames/s · {dropped} dropped · {frames.length}/2000 buffered
        </span>
      </div>
      <div class="can-filters">
        <label>
          ID{' '}
          <input
            value={filters.id}
            placeholder="e.g. 18FF"
            onInput={(event) => setFilters({ ...filters, id: event.currentTarget.value })}
          />
        </label>
        <label>
          Payload{' '}
          <input
            value={filters.payload}
            placeholder="hex or text"
            onInput={(event) => setFilters({ ...filters, payload: event.currentTarget.value })}
          />
        </label>
        <span>{visibleFrames.length} shown</span>
      </div>
      {detail && (
        <div class="operation-error" role="alert">
          {detail}
        </div>
      )}
      {!supported && <div class="operation-empty">CAN streaming is unavailable in this agent.</div>}
      {supported && (
        <div class="can-feed" aria-live="off">
          {visibleFrames.length === 0 ? (
            <span>No matching frames.</span>
          ) : (
            visibleFrames.map((frame, index) => (
              <div key={`${frame.sequence ?? frame.timestamp ?? 'frame'}-${index}`} class="can-frame">
                <time>{frame.timestamp ? new Date(frame.timestamp).toLocaleTimeString() : '—'}</time>
                <strong>{frame.id}</strong>
                <code>{frame.data}</code>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SshKeyPanel({ device, status }: { device: FleetDevice; status: ExtendedDeviceStatus }) {
  const [value, setValue] = useState('');
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [validation, setValidation] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const supported = status.capabilities?.sshKeyInstall === true;

  useEffect(() => {
    if (!value.trim()) {
      setFingerprint(null);
      setValidation(null);
      return;
    }
    let cancelled = false;
    void sshFingerprint(value).then(
      (next) => {
        if (!cancelled) {
          setFingerprint(next);
          setValidation(null);
        }
      },
      (cause) => {
        if (!cancelled) {
          setFingerprint(null);
          setValidation(cause instanceof Error ? cause.message : 'Invalid key');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [value]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.name.endsWith('.pub')) {
      setValidation('Choose an OpenSSH .pub file.');
      return;
    }
    setValue(await file.text());
  };
  const confirmInstall = () => {
    let publicKey: string;
    try {
      publicKey = parseOpenSshPublicKey(value).normalized;
    } catch (cause) {
      setValidation(cause instanceof Error ? cause.message : 'Invalid key');
      return;
    }
    setPendingKey(publicKey);
  };
  const install = async (publicKey: string) => {
    setWorking(true);
    setResult(null);
    try {
      await installDeveloperSshKey(device.id, publicKey);
      setResult('Key installation requested. Existing keys will be preserved and duplicates ignored.');
    } catch (cause) {
      throw cause instanceof Error ? cause : new Error('Could not install the key');
    } finally {
      setWorking(false);
    }
  };
  return (
    <div class="ssh-panel">
      <label class="ssh-upload">
        <Icon name="upload" size={15} /> Choose .pub file
        <input
          type="file"
          accept=".pub,text/plain"
          onChange={(event) => void upload(event.currentTarget.files?.[0])}
        />
      </label>
      <textarea
        value={value}
        rows={3}
        spellcheck={false}
        placeholder="Paste an ssh-ed25519, ssh-rsa, or ECDSA public key…"
        onInput={(event) => setValue(event.currentTarget.value)}
      />
      {fingerprint && (
        <div class="key-fingerprint">
          <span>Fingerprint</span>
          <code>{fingerprint}</code>
        </div>
      )}
      {validation && (
        <div class="operation-error" role="alert">
          {validation}
        </div>
      )}
      <button
        class="operation-primary"
        type="button"
        disabled={!supported || device.presence !== 'online' || !fingerprint || working}
        onClick={confirmInstall}
      >
        {working ? <span class="mini-spinner" /> : <Icon name="key" size={14} />} Install for developer
      </button>
      {!supported && <span class="capability-note">SSH key installation is unavailable in this agent.</span>}
      {result && (
        <span class="action-result" role="status">
          {result}
        </span>
      )}
      {pendingKey && (
        <ConfirmationDialog
          title="Install SSH key?"
          description={`This grants the key access to developer@${deviceLabel(device)}. Existing keys are preserved and an identical key will not be added twice.`}
          detailLabel="Fingerprint"
          detail={fingerprint ?? 'Unknown'}
          confirmLabel="Install key"
          icon="key"
          onClose={() => setPendingKey(null)}
          onConfirm={() => install(pendingKey)}
        />
      )}
    </div>
  );
}

function OperationalDetails({ device, status }: { device: FleetDevice; status: ExtendedDeviceStatus }) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const operational = readOperationalStatus(status);
  const refresh = async () => {
    if (device.presence !== 'online' || refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      await refreshOperationalStatus(device.id);
    } catch (cause) {
      setRefreshError(cause instanceof Error ? cause.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };
  return (
    <div class="operations-area">
      <div class="operations-heading">
        <div>
          <span>Operations</span>
          <small>
            {operational?.collectedAt
              ? `Collected ${formatRelative(operational.collectedAt, Date.now())}`
              : 'Waiting for data'}{' '}
            · automatic every 30s
          </small>
        </div>
        <button
          type="button"
          disabled={device.presence !== 'online' || refreshing}
          onClick={() => void refresh()}
        >
          <Icon name="refresh" size={14} />
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>
      {refreshError && (
        <div class="operation-error" role="alert">
          {refreshError}
        </div>
      )}
      <div class="operation-card">
        <h3>
          <Icon name="bluetooth" size={16} /> Bluetooth
        </h3>
        <BluetoothPanel status={status} />
      </div>
      <div class="operation-card wide">
        <h3>Services</h3>
        <ServicesPanel device={device} status={status} />
      </div>
      <div class="operation-card wide">
        <h3>
          <Icon name="can" size={16} /> Live CAN
        </h3>
        <CanMonitor device={device} status={status} />
      </div>
      <div class="operation-card wide">
        <h3>
          <Icon name="key" size={16} /> Developer SSH key
        </h3>
        <SshKeyPanel device={device} status={status} />
      </div>
    </div>
  );
}

function DeviceDetails({ device }: { device: FleetDevice }) {
  const status = device.status;
  if (!status) return <div class="empty-details">Waiting for the first status report.</div>;
  const networks = usefulNetworkInterfaces(status.interfaces);
  const cpu = status.cpuModule;
  const manikinSerial = distinctManikinSerial(status.serial, cpu?.productSerial);
  const unavailable = 'Unavailable';
  return (
    <div class="device-details">
      <div class="platform-grid">
        <div>
          <span>Operating system</span>
          <strong>
            {status.osName} {status.osVersion ?? ''}
          </strong>
        </div>
        <div>
          <span>Kernel</span>
          <strong title={status.kernelVersion}>{status.kernelVersion}</strong>
        </div>
        <div>
          <span>Architecture</span>
          <strong>{status.architecture}</strong>
        </div>
        <div>
          <span>Python</span>
          <strong>{status.pythonVersion ?? 'Unavailable'}</strong>
        </div>
        <div>
          <span>Uptime</span>
          <strong>{formatUptime(status.uptimeSeconds)}</strong>
        </div>
        <div>
          <span>Agent</span>
          <strong>{status.agentVersion}</strong>
        </div>
      </div>
      <div class="section-label">CPU module</div>
      <div class="platform-grid cpu-module-grid">
        <div>
          <span>System image</span>
          <strong>{cpu?.systemVersion ?? unavailable}</strong>
        </div>
        <div>
          <span>MCBApp</span>
          <strong>{cpu?.mcbappVersion ?? unavailable}</strong>
        </div>
        <div>
          <span>MCBApp build</span>
          <strong title={cpu?.mcbappBuildStamp}>{cpu?.mcbappBuildStamp ?? unavailable}</strong>
        </div>
        <div>
          <span>Board controller</span>
          <strong>{cpu?.boardControllerVersion ?? unavailable}</strong>
        </div>
        <div>
          <span>System controller</span>
          <strong>{cpu?.systemControllerVersion ?? unavailable}</strong>
        </div>
        <div>
          <span>Controller build</span>
          <strong>{cpu?.systemControllerBuild ?? unavailable}</strong>
        </div>
        <div>
          <span>Controller tag</span>
          <strong title={cpu?.systemControllerTag}>{cpu?.systemControllerTag ?? unavailable}</strong>
        </div>
        <div>
          <span>Hardware</span>
          <strong>
            {cpu?.hardwareId ?? unavailable}
            {cpu?.hardwareRevision ? ` rev ${cpu.hardwareRevision}` : ''}
          </strong>
        </div>
        <div>
          <span>Model number</span>
          <strong>{formatProductId(cpu?.productId)}</strong>
        </div>
        <div>
          <span>Product type code</span>
          <strong>{cpu?.productType ?? ''}</strong>
        </div>
        <div>
          <span>Product date</span>
          <strong>{cpu?.productDate ?? unavailable}</strong>
        </div>
        <div>
          <span>Manikin serial</span>
          <strong>{manikinSerial}</strong>
        </div>
        <div>
          <span>Product serial</span>
          <strong>{cpu?.productSerial ?? ''}</strong>
        </div>
        <div>
          <span>Manikin ID</span>
          <strong>{formatManikinId(cpu?.manikinId ?? status.modelId ?? undefined)}</strong>
        </div>
        <div>
          <span>BLE server</span>
          <strong>{cpu?.bleServerVersion ?? unavailable}</strong>
        </div>
      </div>
      <div class="section-label">Network interfaces</div>
      <div class="interface-list">
        {networks.length > 0 ? (
          networks.map((network) => <InterfaceRow key={network.name} network={network} />)
        ) : (
          <span class="muted">No Wi-Fi or Ethernet interfaces reported.</span>
        )}
      </div>
      <OperationalDetails device={device} status={status as ExtendedDeviceStatus} />
    </div>
  );
}

function DeviceCard({
  device,
  now,
  onRename,
  onReboot,
}: {
  device: FleetDevice;
  now: number;
  onRename: (device: FleetDevice) => void;
  onReboot: (device: FleetDevice) => void;
}) {
  const status = device.status;
  const manikinSerial = distinctManikinSerial(status?.serial, status?.cpuModule?.productSerial);
  const latestCommand = latestCommandFor(device.id, now);
  const commandActive = latestCommand && !terminalCommandStates.has(latestCommand.state);
  return (
    <article class={`device-card presence-${device.presence}`}>
      <div class="device-card-top">
        <div class={`presence-mark ${device.presence}`} role="img" aria-label={device.presence} />
        <div class="device-title">
          <h2>{deviceLabel(device)}</h2>
          <div class="identity-line">
            {device.displayName && status?.hostname && <span>{status.hostname}</span>}
            <code>{device.identityMac}</code>
            {manikinSerial && <span>Manikin serial {manikinSerial}</span>}
            {status?.cpuModule?.productSerial && <span>Product serial {status.cpuModule.productSerial}</span>}
            {status?.modelId && <span>{formatManikinId(status.modelId)}</span>}
          </div>
        </div>
        <button
          class="rename-button"
          type="button"
          aria-label={`Rename ${deviceLabel(device)}`}
          title="Edit display name"
          onClick={() => onRename(device)}
        >
          <Icon name="edit" size={14} />
        </button>
        <span class={`presence-pill ${device.presence}`}>{device.presence}</span>
      </div>

      <DeviceDetails device={device} />

      <footer class="device-footer">
        <div class="last-seen">
          <Icon name="clock" size={15} />
          <span>
            {device.presence === 'online'
              ? 'Seen just now'
              : `Seen ${formatRelative(device.lastSeenAt, now)}`}
          </span>
        </div>
        <div class="device-actions">
          {latestCommand && <CommandBadge command={latestCommand} />}
          <button
            class="reboot-button"
            type="button"
            disabled={device.presence !== 'online' || Boolean(commandActive)}
            onClick={() => onReboot(device)}
            title={device.presence !== 'online' ? 'Offline devices cannot receive commands' : 'Reboot device'}
          >
            <Icon name="power" size={16} />
            Reboot
          </button>
        </div>
      </footer>
    </article>
  );
}

function DeviceTable({
  fleet,
  now,
  onRename,
  onReboot,
}: {
  fleet: FleetDevice[];
  now: number;
  onRename: (device: FleetDevice) => void;
  onReboot: (device: FleetDevice) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div class="table-shell">
      <table class="fleet-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Manikin ID</th>
            <th>Network</th>
            <th>Architecture</th>
            <th>Last seen</th>
            <th>
              <span class="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {fleet.map((device) => {
            const status = device.status;
            const latest = latestCommandFor(device.id, now);
            const active = latest && !terminalCommandStates.has(latest.state);
            const primaryNetwork = primaryNetworkInterface(status?.interfaces);
            const primaryAddress = primaryNetworkAddress(primaryNetwork);
            const activeNetworks = usefulNetworkInterfaces(status?.interfaces).filter(
              (network) => network.up && network.carrier !== false,
            );
            const connectionKinds = [...new Set(activeNetworks.map((network) => network.kind))];
            const connectedSsid = activeNetworks.find((network) => network.kind === 'wifi')?.ssid;
            const manikinId = status?.cpuModule?.manikinId ?? status?.modelId;
            const manikin = manikinName(manikinId);
            const open = expanded === device.id;
            return (
              <Fragment key={device.id}>
                <tr key={device.id} class={open ? 'expanded' : ''}>
                  <td>
                    <button
                      class="row-device"
                      type="button"
                      onClick={() => setExpanded(open ? null : device.id)}
                      aria-expanded={open}
                    >
                      <span
                        class={`presence-mark ${device.presence}`}
                        role="img"
                        aria-label={device.presence}
                        title={device.presence === 'online' ? 'Online' : 'Offline'}
                      />
                      <span>
                        <strong>{deviceLabel(device)}</strong>
                        <code>
                          {device.displayName && status?.hostname ? `${status.hostname} · ` : ''}
                          {device.identityMac}
                        </code>
                      </span>
                      <Icon name="chevron" size={16} />
                    </button>
                  </td>
                  <td>
                    {manikinId ? (
                      <span class={`manikin-chip manikin-chip-${manikinChipTone(manikinId)}`}>
                        {manikin ?? manikinId}
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                  </td>
                  <td>
                    <strong class="network-address">{primaryAddress ?? 'No address'}</strong>
                    <span class="network-connection">
                      {connectedSsid && <span>{connectedSsid}</span>}
                      {connectionKinds.map((kind) => (
                        <span
                          key={kind}
                          class={`connection-kind connection-${kind}`}
                          role="img"
                          aria-label={kind === 'wifi' ? 'Wi-Fi' : 'Ethernet'}
                          title={kind === 'wifi' ? 'Connected over Wi-Fi' : 'Connected over Ethernet'}
                        >
                          <Icon name={kind} size={14} />
                        </span>
                      ))}
                      {connectionKinds.length === 0 && <span>No active network</span>}
                    </span>
                  </td>
                  <td>
                    <strong>{status?.architecture ?? '—'}</strong>
                  </td>
                  <td>
                    {device.presence === 'online' ? 'Just now' : formatRelative(device.lastSeenAt, now)}
                  </td>
                  <td>
                    <div class="table-actions">
                      {latest && <CommandBadge command={latest} />}
                      <button
                        class="icon-button"
                        type="button"
                        aria-label={`Rename ${deviceLabel(device)}`}
                        title="Edit display name"
                        onClick={() => onRename(device)}
                      >
                        <Icon name="edit" size={15} />
                      </button>
                      <button
                        class="icon-button danger"
                        type="button"
                        aria-label={`Reboot ${deviceLabel(device)}`}
                        disabled={device.presence !== 'online' || Boolean(active)}
                        onClick={() => onReboot(device)}
                      >
                        <Icon name="power" size={17} />
                      </button>
                    </div>
                  </td>
                </tr>
                {open && (
                  <tr key={`${device.id}-details`} class="detail-row">
                    <td colSpan={6}>
                      <DeviceDetails device={device} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RebootDialog({
  device,
  onClose,
  onSuccess,
}: {
  device: FleetDevice;
  onClose: () => void;
  onSuccess: (message: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const name = deviceLabel(device);

  useEffect(() => {
    cancelRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && !submitting && onClose();
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, submitting]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await createRebootCommand(device.id);
      onSuccess(`Reboot sent to ${name}`);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not send reboot command');
      setSubmitting(false);
    }
  };

  return (
    <div class="dialog-backdrop">
      <div
        class="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reboot-title"
        aria-describedby="reboot-description"
      >
        <button class="dialog-close" type="button" aria-label="Close" onClick={onClose} disabled={submitting}>
          <Icon name="close" />
        </button>
        <div class="dialog-icon">
          <Icon name="power" size={25} />
        </div>
        <h2 id="reboot-title">Reboot {name}?</h2>
        <p id="reboot-description">
          The device will disconnect immediately and should return online after boot. Commands are never
          queued for offline devices.
        </p>
        <div class="dialog-identity">
          <span>Permanent identity</span>
          <code>{device.identityMac}</code>
        </div>
        {error && (
          <div class="dialog-error" role="alert">
            {error}
          </div>
        )}
        <div class="dialog-actions">
          <button
            ref={cancelRef}
            class="secondary-button"
            type="button"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button class="confirm-button" type="button" disabled={submitting} onClick={submit}>
            {submitting ? <span class="spinner" /> : <Icon name="power" size={16} />}
            {submitting ? 'Sending…' : 'Reboot device'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameDialog({
  device,
  onClose,
  onSuccess,
}: {
  device: FleetDevice;
  onClose: () => void;
  onSuccess: (message: string) => void;
}) {
  const [value, setValue] = useState(device.displayName ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fallbackName = device.status?.hostname ?? device.identityMac;

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && !submitting && onClose();
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, submitting]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const displayName = value.trim() || null;
    try {
      await updateDeviceDisplayName(device.id, displayName);
      onSuccess(displayName ? `Named device “${displayName}”` : `Display name cleared for ${fallbackName}`);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update the display name');
      setSubmitting(false);
    }
  };

  return (
    <div class="dialog-backdrop">
      <form
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-title"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <button class="dialog-close" type="button" aria-label="Close" onClick={onClose} disabled={submitting}>
          <Icon name="close" />
        </button>
        <div class="dialog-icon naming">
          <Icon name="edit" size={24} />
        </div>
        <h2 id="rename-title">Name this device</h2>
        <p>
          This changes only how the device appears in Overmind. Its permanent MAC identity remains unchanged.
        </p>
        <label class="dialog-field">
          <span>Display name</span>
          <input
            ref={inputRef}
            value={value}
            maxLength={80}
            placeholder={fallbackName}
            onInput={(event) => setValue(event.currentTarget.value)}
          />
          <small>Leave empty to use the reported hostname.</small>
        </label>
        <div class="dialog-identity">
          <span>Permanent identity</span>
          <code>{device.identityMac}</code>
        </div>
        {error && (
          <div class="dialog-error" role="alert">
            {error}
          </div>
        )}
        <div class="dialog-actions">
          <button class="secondary-button" type="button" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button class="save-button" type="submit" disabled={submitting}>
            {submitting ? <span class="spinner" /> : <Icon name="edit" size={16} />}
            {submitting ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function App() {
  const auth = useAuth();
  const [query, setQuery] = useState('');
  const [view, setView] = useState<ViewMode>(() => (window.innerWidth < 860 ? 'cards' : 'table'));
  const [renameTarget, setRenameTarget] = useState<FleetDevice | null>(null);
  const [rebootTarget, setRebootTarget] = useState<FleetDevice | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [now, setNow] = useState(Date.now());
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    let disposeSocket = () => {};
    void loadFleet();
    connectFleetSocket((dispose) => {
      disposeSocket = dispose;
    });
    const clock = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      disposeSocket();
      window.clearInterval(clock);
    };
  }, [auth.status]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 5_000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const compact = window.matchMedia('(max-width: 700px)');
    const handleCompactView = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) setView('cards');
    };
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    handleCompactView(compact);
    compact.addEventListener('change', handleCompactView);
    window.addEventListener('keydown', handleShortcut);
    return () => {
      compact.removeEventListener('change', handleCompactView);
      window.removeEventListener('keydown', handleShortcut);
    };
  }, []);

  const filteredDevices = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return sortedDevices.value;
    return sortedDevices.value.filter((device) => {
      const status = device.status;
      return [
        device.identityMac,
        device.displayName,
        device.serial,
        device.modelId,
        status?.hostname,
        status?.osName,
        ...Object.values(status?.cpuModule ?? {}),
        ...(status?.interfaces.map((network) => network.ssid) ?? []),
      ].some((value) => value?.toLowerCase().includes(normalized));
    });
  }, [query, devices.value]);

  if (auth.status !== 'authenticated') {
    if (auth.status === 'error') {
      return (
        <div class="auth-loading" role="alert">
          <strong>Couldn’t check your sign-in status.</strong>
          <button type="button" onClick={auth.retry}>
            Retry
          </button>
        </div>
      );
    }
    return (
      <div class="auth-loading" role="status">
        <span class="spinner" />
        <strong>{auth.status === 'redirecting' ? 'Redirecting to sign in…' : 'Checking sign in…'}</strong>
      </div>
    );
  }
  const user = auth.user;

  const connectionCopy = {
    connecting: 'Connecting',
    live: 'Live',
    reconnecting: 'Reconnecting',
    offline: 'Browser offline',
  }[connectionState.value];

  return (
    <div class="app-shell">
      <header class="topbar">
        <a class="brand" href="/" aria-label="Manikin Overmind home">
          <img class="brand-mark" src="/favicon.svg" alt="" />
          <span>
            <strong>MANIKIN</strong>
            <small>OVERMIND</small>
          </span>
        </a>
        <div class="topbar-controls">
          <div class={`live-status status-${connectionState.value}`} role="status">
            <span class="live-dot" />
            <span>{connectionCopy}</span>
            {lastLiveUpdate.value && (
              <small>updated {formatRelative(lastLiveUpdate.value.toISOString(), now)}</small>
            )}
          </div>
          <div class="user-menu" title={user.username}>
            <span>{user.name ?? user.username}</span>
            <a href="/auth/signout">Sign out</a>
          </div>
        </div>
      </header>

      <main>
        <section class="hero">
          <div>
            <p class="eyebrow">Fleet control</p>
            <h1>Device overview</h1>
            <p>
              <a href="https://www.youtube.com/watch?v=kOBtzjS_mvk" target="_blank" rel="noreferrer">
                I live for the swarm
              </a>
            </p>
          </div>
          <fieldset class="fleet-metrics">
            <legend class="sr-only">Fleet totals</legend>
            <div>
              <strong>{onlineCount.value}</strong>
              <span>Online</span>
            </div>
            <div>
              <strong>{devices.value.length - onlineCount.value}</strong>
              <span>Offline</span>
            </div>
            <div>
              <strong>{devices.value.length}</strong>
              <span>Total</span>
            </div>
          </fieldset>
        </section>

        <section class="toolbar" aria-label="Fleet controls">
          <label class="search-box">
            <span class="sr-only">Search devices</span>
            <Icon name="search" />
            <input
              ref={searchRef}
              value={query}
              onInput={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search name, hostname, MAC, serial, SSID…"
            />
            <kbd>⌘ K</kbd>
          </label>
          <button
            class="refresh-button"
            type="button"
            onClick={() => void loadFleet()}
            disabled={loading.value}
          >
            <Icon name="refresh" />
            Refresh
          </button>
          <fieldset class="view-toggle">
            <legend class="sr-only">View style</legend>
            <button
              type="button"
              class={view === 'cards' ? 'active' : ''}
              aria-label="Card view"
              aria-pressed={view === 'cards'}
              onClick={() => setView('cards')}
            >
              <Icon name="grid" />
            </button>
            <button
              type="button"
              class={view === 'table' ? 'active' : ''}
              aria-label="Table view"
              aria-pressed={view === 'table'}
              onClick={() => setView('table')}
            >
              <Icon name="list" />
            </button>
          </fieldset>
        </section>

        {loadError.value && (
          <div class="error-banner" role="alert">
            <span>
              REST snapshot unavailable: {loadError.value}. Live updates will continue reconnecting.
            </span>
            <button type="button" onClick={() => void loadFleet()}>
              Try again
            </button>
          </div>
        )}

        {loading.value && devices.value.length === 0 ? (
          <div class="loading-grid" role="status" aria-label="Loading fleet">
            {[1, 2, 3].map((key) => (
              <div key={key} class="skeleton-card" />
            ))}
          </div>
        ) : filteredDevices.length === 0 ? (
          <div class="empty-state">
            <div class="empty-orbit">
              <span />
            </div>
            <h2>{devices.value.length ? 'No devices match' : 'Waiting for devices'}</h2>
            <p>
              {devices.value.length
                ? 'Try a name, hostname, MAC address, serial, or SSID.'
                : 'Enrolled devices will appear here as soon as they connect.'}
            </p>
          </div>
        ) : view === 'cards' ? (
          <section class="device-grid" aria-label="Fleet devices">
            {filteredDevices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                now={now}
                onRename={setRenameTarget}
                onReboot={setRebootTarget}
              />
            ))}
          </section>
        ) : (
          <DeviceTable
            fleet={filteredDevices}
            now={now}
            onRename={setRenameTarget}
            onReboot={setRebootTarget}
          />
        )}
      </main>

      {renameTarget && (
        <RenameDialog
          device={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSuccess={(message) => setToast({ kind: 'success', message })}
        />
      )}

      {rebootTarget && (
        <RebootDialog
          device={rebootTarget}
          onClose={() => setRebootTarget(null)}
          onSuccess={(message) => setToast({ kind: 'success', message })}
        />
      )}
      {toast && (
        <div class={`toast toast-${toast.kind}`} role="status">
          <span class="toast-check">✓</span>
          {toast.message}
          <button type="button" aria-label="Dismiss notification" onClick={() => setToast(null)}>
            <Icon name="close" size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
